// Shim de `firebase/firestore` — reproduit l'API Firestore utilisée par l'app,
// branchée sur notre serveur Express :
//   • Docs "V1"      : doc(db,"appdata",key)  -> /api/appdata/:key  ({value,...})
//   • Collections    : collection/doc/query   -> /api/col/:collection[/:id]
//   • Transactions   : runTransaction          -> /api/tx (concurrence optimiste)
//
// Objectif : le code applicatif (repris de Melissa) reste inchangé.

const API = "";
const POLL_MS = 3000;

// ── Génération d'ID type Firestore (20 car. alphanumériques) ────────────────
const ID_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
function genId() {
  let s = "";
  for (let i = 0; i < 20; i++) s += ID_CHARS[Math.floor(Math.random() * ID_CHARS.length)];
  return s;
}

const isAppdata = (coll) => coll === "appdata";
const docUrl = (ref) => isAppdata(ref.collection)
  ? `${API}/api/appdata/${encodeURIComponent(ref.id)}`
  : `${API}/api/col/${encodeURIComponent(ref.collection)}/${encodeURIComponent(ref.id)}`;
const collUrl = (name) => `${API}/api/col/${encodeURIComponent(name)}`;

// ── Références ───────────────────────────────────────────────────────────────
export function getFirestore() { return { __epdb: true }; }

// doc(db, collection, id?)  |  doc(collectionRef)  (id auto)
export function doc(a, b, c) {
  if (a && a.__coll) {
    return { __ref: true, collection: a.collection, id: genId() };
  }
  const collection = b;
  const id = c != null ? String(c) : genId();
  return { __ref: true, collection, id };
}

export function collection(_db, name) {
  return { __coll: true, collection: name };
}

export function query(ref, ...constraints) {
  return { __query: true, collection: ref.collection, constraints };
}

export function where(field, op, value) { return { __c: "where", field, op, value }; }
export function orderBy(field, dir = "asc") { return { __c: "orderBy", field, dir }; }

// ── Snapshots ────────────────────────────────────────────────────────────────
function docSnapshot(exists, data, id, rev) {
  return {
    id,
    _rev: rev,
    exists: () => !!exists,
    data: () => (exists ? data : undefined),
  };
}
function querySnapshot(rows) {
  const docs = rows.map((r) => {
    const { id, ...rest } = r;
    return { id, data: () => rest, exists: () => true };
  });
  return {
    docs,
    size: docs.length,
    empty: docs.length === 0,
    forEach: (fn) => docs.forEach(fn),
  };
}

// ── Contraintes (filtrage/tri côté client) ──────────────────────────────────
function cmp(v, op, val) {
  switch (op) {
    case "==": return v === val;
    case "!=": return v !== val;
    case ">": return v > val;
    case ">=": return v >= val;
    case "<": return v < val;
    case "<=": return v <= val;
    case "in": return Array.isArray(val) && val.includes(v);
    case "array-contains": return Array.isArray(v) && v.includes(val);
    default: return true;
  }
}
function applyConstraints(arr, constraints = []) {
  let out = arr.slice();
  for (const c of constraints) {
    if (c.__c === "where") out = out.filter((row) => cmp(row[c.field], c.op, c.value));
  }
  for (const c of constraints) {
    if (c.__c === "orderBy") {
      const dir = c.dir === "desc" ? -1 : 1;
      out.sort((a, b) => {
        const av = a[c.field], bv = b[c.field];
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
  }
  return out;
}

// ── Lectures ponctuelles ─────────────────────────────────────────────────────
export async function getDoc(ref) {
  try {
    const res = await fetch(docUrl(ref), { credentials: "include" });
    if (isAppdata(ref.collection)) {
      if (res.status === 404) return docSnapshot(false, undefined, ref.id);
      if (!res.ok) return docSnapshot(false, undefined, ref.id);
      const body = await res.json();
      return docSnapshot(true, body, ref.id);
    }
    if (!res.ok) return docSnapshot(false, undefined, ref.id);
    const j = await res.json();
    return docSnapshot(!!j.exists, j.data, ref.id, j.rev);
  } catch {
    return docSnapshot(false, undefined, ref.id);
  }
}

export async function getDocs(target) {
  const name = target.collection;
  const constraints = target.constraints || [];
  const res = await fetch(collUrl(name), { credentials: "include" });
  const j = res.ok ? await res.json() : { value: [] };
  return querySnapshot(applyConstraints(j.value || [], constraints));
}

// ── Écritures ────────────────────────────────────────────────────────────────
function jsonInit(method, body) {
  return {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export async function setDoc(ref, data, options) {
  if (isAppdata(ref.collection)) {
    const res = await fetch(docUrl(ref), jsonInit("PUT", data));
    if (!res.ok) throw new Error("setDoc " + res.status);
    return true;
  }
  const merge = !!(options && options.merge);
  const res = await fetch(docUrl(ref), jsonInit(merge ? "PATCH" : "PUT", data));
  if (!res.ok) throw new Error("setDoc " + res.status);
  return true;
}

export async function updateDoc(ref, data) {
  const res = await fetch(docUrl(ref), jsonInit("PATCH", data));
  if (!res.ok) throw new Error("updateDoc " + res.status);
  return true;
}

// ── onSnapshot (doc OU collection/query) — polling ──────────────────────────
export function onSnapshot(target, onNext, onError) {
  let stopped = false;
  let lastStamp = null;

  const isCollection = target.__coll || target.__query;

  async function tick() {
    if (stopped) return;
    try {
      if (isCollection) {
        const res = await fetch(collUrl(target.collection), { credentials: "include" });
        if (res.ok) {
          const body = await res.json();
          const stamp = JSON.stringify(body.value || []);
          if (stamp !== lastStamp) {
            lastStamp = stamp;
            const rows = applyConstraints(body.value || [], target.constraints || []);
            onNext(querySnapshot(rows));
          }
        } else if (res.status === 401 && onError) {
          onError(new Error("permission-denied"));
        }
      } else {
        const res = await fetch(docUrl(target), { credentials: "include" });
        if (isAppdata(target.collection)) {
          if (res.ok) {
            const body = await res.json();
            const stamp = body && body.updatedAt != null ? String(body.updatedAt) : JSON.stringify(body);
            if (stamp !== lastStamp) { lastStamp = stamp; onNext(docSnapshot(true, body, target.id)); }
          }
          // 404 → doc absent : on ne notifie pas (parité avec le comportement d'origine)
        } else if (res.ok) {
          const j = await res.json();
          const stamp = JSON.stringify(j);
          if (stamp !== lastStamp) { lastStamp = stamp; onNext(docSnapshot(!!j.exists, j.data, target.id, j.rev)); }
        }
      }
    } catch (e) {
      // réseau : on retente au prochain tick (pas d'onError pour éviter le bruit)
    }
    if (!stopped) setTimeout(tick, POLL_MS);
  }
  tick();
  return () => { stopped = true; };
}

// ── Transactions (concurrence optimiste via /api/tx) ────────────────────────
export async function runTransaction(_db, updateFn) {
  for (let attempt = 0; attempt < 6; attempt++) {
    const reads = [];
    const writes = [];
    const tx = {
      async get(ref) {
        const res = await fetch(docUrl(ref), { credentials: "include" });
        let exists = false, data, rev = null;
        if (res.ok) { const j = await res.json(); exists = !!j.exists; data = j.data; rev = j.rev ?? null; }
        reads.push({ collection: ref.collection, id: ref.id, rev });
        return docSnapshot(exists, data, ref.id, rev);
      },
      set(ref, data, options) {
        writes.push({ collection: ref.collection, id: ref.id, op: "set", data, merge: !!(options && options.merge) });
      },
      update(ref, data) {
        writes.push({ collection: ref.collection, id: ref.id, op: "update", data });
      },
    };
    // eslint-disable-next-line no-await-in-loop
    await updateFn(tx);
    // eslint-disable-next-line no-await-in-loop
    const res = await fetch(`${API}/api/tx`, jsonInit("POST", { reads, writes }));
    // eslint-disable-next-line no-await-in-loop
    const jr = res.ok ? await res.json() : { ok: false, error: "tx " + res.status };
    if (jr.ok) return;
    if (!jr.conflict) throw new Error(jr.error || "transaction échouée");
    // conflit → nouvelle tentative
  }
  throw new Error("transaction : trop de conflits, réessayez.");
}

export default {
  getFirestore, doc, collection, query, where, orderBy,
  getDoc, getDocs, setDoc, updateDoc, onSnapshot, runTransaction,
};
